import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router'
import { observer } from 'mobx-react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

// evertel
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { Confirm2FAEnrollmentModal } from '@evertel/web/security'
import { InfoBox, Button, Card, CardBody, Col, Row, FormInput, PasswordInput, Logo } from '@evertel/web/ui'
import { Analytics } from '@evertel/analytics'


const Enroll2FALinkPage = observer(() => {
    const { token } = useParams()

    const analytics = useService(Analytics)
    const sessionService = useService(SessionService)
    const authenticationController = useService(AuthenticationController)

    const navigate = useNavigate()

    const [phoneNumber, setPhoneNumber] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [showConfirmEnrollmentModal, setShowConfirmEnrollmentModal] = useState(false)

    useEffect(() => {
        if (!token) {
            setError('Unable to retrieve enrollment token. Please click on your enrollment link to try again.')
        }

        analytics.logView('/enroll-mfa')
    }, [token])

    const redirectToLogin = () => {
        navigate('/login')
    }

    const onEnrollmentComplete = useCallback(() => {
        setShowConfirmEnrollmentModal(false)
        if (sessionService.currentUserId) {
            navigate('/landing')
        } else {
            navigate('/login')
        }
    }, [sessionService.currentUserId, navigate])

    const submitEnrollMfa = useCallback(async () => {
        setError('')

        if (!phoneNumber?.trim() || !password?.trim()) {
            setError('Please fill out all fields')
            return
        }

        const parsedNumber = parsePhoneNumberFromString(phoneNumber, 'US')

        if (!parsedNumber?.isValid()) {
            setError('Invalid phone number')
            return
        }

        setIsLoading(true)

        try {
            const body = {
                phoneNumber: parsedNumber.number,
                password: password.trim()
            }

            const response = await authenticationController.enrollMFAToken(body, token)
            authenticationController.setMFAParams(response.access_token, response.oob_code)

            setShowConfirmEnrollmentModal(true)
        } catch (err: any) {
            setError(authenticationController.formatEnrollmentError(err))
        } finally {
            setIsLoading(false)
        }
    }, [phoneNumber, password, token, authenticationController])

    return (
        <div className="app auth-layout fadeIn animated" style={{ height: '100vh', overflow: 'auto' }}>
            <Row align="center" valign="center" className="p-4" style={{ minHeight: '100%' }}>
                <Card className="p-4" style={{ maxWidth: 650 }}>
                    <CardBody>
                        <div className="text-center mb-4">
                            <Logo className="mb-4" />
                            <h1>Enroll in SMS Authentication</h1>
                        </div>

                        {error && (
                            <InfoBox color="danger">
                                {error}
                            </InfoBox>
                        )}

                        {/* PHONE NUMBER */}
                        <FormInput
                            label="Mobile Number"
                            type="text"
                            name="phoneNumber"
                            autoComplete="off"
                            placeholder="Type your mobile phone number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            required={true}
                            leftIcon="phone"
                        />

                        {/* PASSWORD */}
                        <PasswordInput
                            label="Current Password"
                            name="password"
                            className="mb-4"
                            value={password}
                            disabled={isLoading}
                            onChange={setPassword}
                            placeholder="Type your current password"
                        />

                        <Row align="end" valign="center" className="mt-4">
                            <Button
                                color="link"
                                className="me-3"
                                onClick={redirectToLogin}
                            >
                                Back to login
                            </Button>

                            <Button
                                color="primary"
                                type="submit"
                                loading={isLoading}
                                disabled={isLoading}
                                onClick={submitEnrollMfa}
                            >
                                Enroll
                            </Button>
                        </Row>
                    </CardBody>
                </Card>
            </Row>

            {showConfirmEnrollmentModal && (
                <Confirm2FAEnrollmentModal
                    isOpen={showConfirmEnrollmentModal}
                    onClose={() => setShowConfirmEnrollmentModal(false)}
                    onComplete={onEnrollmentComplete}
                    phoneNumber={phoneNumber}
                />
            )}
        </div>
    )
})

export { Enroll2FALinkPage }
